"use client"

import { useState, useEffect } from "react"
import { LuUsers, LuShield } from "react-icons/lu"
import { Link, useLocation } from "react-router-dom"
import { IoDocumentTextOutline } from "react-icons/io5"
import { BsCardHeading } from "react-icons/bs"
import { LiaShippingFastSolid } from "react-icons/lia"
import { FaShop } from "react-icons/fa6"

function Sidebar() {
  const location = useLocation()
  const [isMinimized, setIsMinimized] = useState(() => {
    const saved = localStorage.getItem("sidebarMinimized")
    return saved ? JSON.parse(saved) : false
  })

  const isActive = (paths) => paths.includes(location.pathname)

  useEffect(() => {
    if (isMinimized) {
      document.body.classList.add("toggle-sidebar")
    } else {
      document.body.classList.remove("toggle-sidebar")
    }
  }, [isMinimized])

  useEffect(() => {
    const handleToggle = () => {
      setIsMinimized((prev) => {
        const newState = !prev
        localStorage.setItem("sidebarMinimized", JSON.stringify(newState))
        return newState
      })
    }

    const toggleButton = document.querySelector(".toggle-sidebar-btn")
    if (toggleButton) {
      toggleButton.addEventListener("click", handleToggle)
      return () => toggleButton.removeEventListener("click", handleToggle)
    }
  }, [])

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        {/* Dashboard */}
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/"]) ? "active" : "collapsed"}`} to="/">
            <i className="bi bi-grid"></i>
            <span>Dashboard</span>
          </Link>
        </li>

        {/* Orders ----------------------------------------- */}

        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/orders"]) ? "active" : "collapsed"}`} to="/orders">
            <i className="bi bi-bag-check"></i>
            <span>Orders</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/users"]) ? "active" : "collapsed"}`} to="/users">
            <i>
              <LuUsers />
            </i>
            <span>Users</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/admins"]) ? "active" : "collapsed"}`} to="/admins">
            <i>
              <LuShield />
            </i>
            <span>Admins</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/blogs"]) ? "active" : "collapsed"}`} to="/blogs">
            <i>
              <IoDocumentTextOutline />
            </i>
            <span>Blogs</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/banner"]) ? "active" : "collapsed"}`} to="/banner">
            <i>
              <BsCardHeading />
            </i>
            <span>Banner</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/shipping-zone"]) ? "active" : "collapsed"}`} to="/shipping-zone">
            <i>
              <LiaShippingFastSolid />
            </i>
            <span>Shipping zone</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/shop-location"]) ? "active" : "collapsed"}`} to="/shop-location">
            <i>
              <FaShop />
            </i>
            <span>Shop location</span>
          </Link>
        </li>

        {/* Category */}
        <li className="nav-item">
          <Link
            className={`nav-link ${isActive(["/addcategory", "/category-list"]) ? "" : "collapsed"}`}
            data-bs-toggle="collapse"
            data-bs-target="#category"
            aria-expanded={isActive(["/addcategory", "/category-list"])}
            to="#"
          >
            <i className="bi bi-tags"></i>
            <span>Category</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </Link>
          <ul
            id="category"
            className={`nav-content collapse ${isActive(["/addcategory", "/category-list"]) ? "show" : ""}`}
            data-bs-parent="#sidebar-nav"
          >
            <li>
              <Link className={isActive(["/addcategory"]) ? "active" : ""} to="/addcategory">
                <i className="bi bi-circle"></i>
                <span>Add Category</span>
              </Link>
            </li>
            <li>
              <Link className={isActive(["/category-list"]) ? "active" : ""} to="/category-list">
                <i className="bi bi-circle"></i>
                <span>Category List</span>
              </Link>
            </li>
          </ul>
        </li>

        {/* Sub Category */}
        <li className="nav-item">
          <Link
            className={`nav-link ${isActive(["/sub-category/create", "/sub-category/list"]) ? "" : "collapsed"}`}
            data-bs-toggle="collapse"
            data-bs-target="#sub-category"
            aria-expanded={isActive(["/sub-category/create", "/sub-category/list"])}
            to="#"
          >
            <i className="bi bi-tags"></i>
            <span>Sub Category</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </Link>
          <ul
            id="sub-category"
            className={`nav-content collapse ${isActive(["/sub-category/create", "/sub-category/list"]) ? "show" : ""}`}
            data-bs-parent="#sidebar-nav"
          >
            <li>
              <Link className={isActive(["/sub-category/create"]) ? "active" : ""} to="/sub-category/create">
                <i className="bi bi-circle"></i>
                <span>Add Sub Category</span>
              </Link>
            </li>
            <li>
              <Link className={isActive(["/sub-category/list"]) ? "active" : ""} to="/sub-category/list">
                <i className="bi bi-circle"></i>
                <span>Sub Category List</span>
              </Link>
            </li>
          </ul>
        </li>

        {/* Products */}
        <li className="nav-item">
          <Link
            className={`nav-link ${isActive(["/addproduct", "/productlist"]) ? "" : "collapsed"}`}
            data-bs-toggle="collapse"
            data-bs-target="#products"
            aria-expanded={isActive(["/addproduct", "/productlist"])}
            to="#"
          >
            <i className="bi bi-box"></i>
            <span>Products</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </Link>
          <ul
            id="products"
            className={`nav-content collapse ${isActive(["/addproduct", "/productlist"]) ? "show" : ""}`}
            data-bs-parent="#sidebar-nav"
          >
            <li>
              <Link className={isActive(["/addproduct"]) ? "active" : ""} to="/addproduct">
                <i className="bi bi-circle"></i>
                <span>Add Product</span>
              </Link>
            </li>
            <li>
              <Link className={isActive(["/productlist"]) ? "active" : ""} to="/productlist">
                <i className="bi bi-circle"></i>
                <span>Product List</span>
              </Link>
            </li>
          </ul>
        </li>

        {/* Coupons */}
        <li className="nav-item">
          <Link
            className={`nav-link ${isActive(["/addcoupon", "/couponlist", "/coupons", "/coupon/create"]) ? "" : "collapsed"}`}
            data-bs-toggle="collapse"
            data-bs-target="#coupon"
            aria-expanded={isActive(["/addcoupon", "/couponlist", "/coupons", "/coupon/create"])}
            to="#"
          >
            <i className="bi bi-ticket"></i>
            <span>Coupon</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </Link>
          <ul
            id="coupon"
            className={`nav-content collapse ${isActive(["/coupon/create", "/coupons"]) ? "show" : ""}`}
            data-bs-parent="#sidebar-nav"
          >
            <li>
              <Link className={isActive(["/coupon/create"]) ? "active" : ""} to="/coupon/create">
                <i className="bi bi-circle"></i>
                <span>Add Coupon</span>
              </Link>
            </li>
            <li>
              <Link className={isActive(["/coupons"]) ? "active" : ""} to="/coupons">
                <i className="bi bi-circle"></i>
                <span>Coupon List</span>
              </Link>
            </li>
          </ul>
        </li>

        {/* Product display Shift */}
        <li className="nav-item">
          <Link className={`nav-link ${isActive(["/shifts"]) ? "active" : "collapsed"}`} to="/shifts">
            <i className="bi bi-clock"></i>
            <span>Product display Shift</span>
          </Link>
        </li>
      </ul>
    </aside>
  )
}

export default Sidebar
